import { useEffect } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import {
  defaultLocale,
  getLocaleDirection,
  isSupportedLocale,
  type PortalLocale,
} from "@/lib/i18n/portal";
import { SellerIntlProvider } from "@/lib/i18n/intl";
import { ToastProvider } from "@a2z/ui";
import { useApplyTheme } from "@/shared/theme/useTheme";

export function getResolvedSellerLocale(value: string | undefined): PortalLocale {
  return value && isSupportedLocale(value) ? value : defaultLocale;
}

export function useLocale() {
  const { locale } = useParams();
  return getResolvedSellerLocale(locale);
}

/**
 * Catch-all for unmatched paths *inside* a locale segment. Without this, they
 * escape to the router's root `*` route, which is locale-blind and would drop
 * an English user into Arabic.
 */
export function LocaleFallbackRedirect() {
  const { locale } = useParams();
  return <Navigate to={`/${getResolvedSellerLocale(locale)}/login`} replace />;
}

export function SellerLocaleLayout() {
  const { locale } = useParams();
  const location = useLocation();
  const resolvedLocale = getResolvedSellerLocale(locale);
  useApplyTheme();

  // Keep the document root `<html>` in sync with the active locale. Chrome's
  // page-translation heuristic reads the ROOT lang/dir (not the inner
  // `.seller-app` wrapper); leaving it stale makes the browser see an
  // English-declared page full of Arabic text and auto-translate it into
  // garbled MSA. Syncing here keeps the root honest for both `en` and `ar`.
  useEffect(() => {
    document.documentElement.lang = resolvedLocale;
    document.documentElement.dir = getLocaleDirection(resolvedLocale);
  }, [resolvedLocale]);

  if (locale !== resolvedLocale) {
    const suffix = location.pathname.replace(/^\/[^/]+/, "") || "/login";
    return <Navigate to={`/${resolvedLocale}${suffix}${location.search}`} replace />;
  }

  return (
    <SellerIntlProvider locale={resolvedLocale}>
      <ToastProvider>
        <div className="seller-app" lang={resolvedLocale} dir={getLocaleDirection(resolvedLocale)}>
          <Outlet />
        </div>
      </ToastProvider>
    </SellerIntlProvider>
  );
}
